import React, { useEffect, useState } from "react";
import API_BASE from "../config/api";
import { adminFetch } from "../utils/adminAuth";

// AuthDialNepal is a separate app (real agent accounts, wallet/commission
// hierarchy) — see backend/database/core/system_config.php there for the
// same constant.
const AUTH_ADMIN_BASE = "https://auth.dialnepal.com.np";

/*
  CR U11 — "Become Agent" management tool.
  Reviews "Become an Agent" form submissions (agent_enquiry). Approving
  here only records the decision — it does NOT create a real agent
  account, since that requires fields this short form never collected
  (agent type/area, province/district, ID proof, a password, etc.).
  "Create Agent →" opens AuthDialNepal's Add Agent form in a new tab with
  name/email/mobile pre-filled, so the admin just fills in the rest.
*/
export default function AdminBecomeAgent() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notes, setNotes] = useState({});
    const [savingId, setSavingId] = useState(null);

    const load = () => {
        setLoading(true);
        adminFetch(`${API_BASE}/api/get_agent_enquiries.php`)
            .then((r) => r.json())
            .then((data) => { if (data.success) setRows(data.enquiries); })
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const setStatus = async (id, status) => {
        setSavingId(id);
        try {
            const res = await adminFetch(`${API_BASE}/api/update_agent_enquiry.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id, status, admin_note: notes[id] ?? "" }),
            });
            const data = await res.json();
            if (data.success) {
                setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status, admin_note: notes[id] ?? r.admin_note } : r)));
            }
        } finally {
            setSavingId(null);
        }
    };

    const createAgentUrl = (row) => {
        const parts = (row.full_name || "").trim().split(/\s+/);
        const fname = parts[0] || "";
        const lname = parts.length > 1 ? parts.slice(1).join(" ") : "";
        const params = new URLSearchParams({
            prefill_fname: fname,
            prefill_lname: lname,
            prefill_email: row.email || "",
            prefill_mobile: row.mobile || "",
            prefill_note: `From website enquiry — city: ${row.city || "n/a"}, experience: ${row.experience || "n/a"}`,
        });
        return `${AUTH_ADMIN_BASE}/agents/add?${params.toString()}`;
    };

    const badge = (status) => {
        const map = { pending: "warning", approved: "success", rejected: "danger" };
        return <span className={`badge bg-${map[status] || "secondary"}`}>{status}</span>;
    };

    return (
        <div>
            <h5 className="fw-bold mb-1">Become Agent — Enquiries</h5>
            <p className="text-secondary small mb-3">
                Submissions from the site's "Become an Agent" form. Approving here doesn't create the real agent account — use
                "Create Agent" to open a pre-filled form in Auth Admin once you're ready.
            </p>

            <div className="card shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="p-3">Loading...</div>
                    ) : rows.length === 0 ? (
                        <div className="p-3 text-secondary">No enquiries yet.</div>
                    ) : (
                        <table className="table table-sm mb-0 align-middle">
                            <thead className="table-light">
                                <tr><th>Name</th><th>Contact</th><th>City</th><th>Experience</th><th>Status</th><th>Note</th><th></th></tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id}>
                                        <td className="fw-semibold">{r.full_name}</td>
                                        <td className="small">{r.mobile}<br /><span className="text-secondary">{r.email}</span></td>
                                        <td>{r.city}</td>
                                        <td className="small">{r.experience}</td>
                                        <td>{badge(r.status)}</td>
                                        <td style={{ minWidth: 160 }}>
                                            <input
                                                className="form-control form-control-sm"
                                                placeholder="Admin note"
                                                defaultValue={r.admin_note}
                                                onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                                            />
                                        </td>
                                        <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                                            <div className="btn-group btn-group-sm mb-1">
                                                <button className="btn btn-outline-success" disabled={savingId === r.id} onClick={() => setStatus(r.id, "approved")}>Approve</button>
                                                <button className="btn btn-outline-danger" disabled={savingId === r.id} onClick={() => setStatus(r.id, "rejected")}>Reject</button>
                                            </div>
                                            <br />
                                            <a className="btn btn-sm btn-outline-primary" href={createAgentUrl(r)} target="_blank" rel="noopener noreferrer">
                                                Create Agent →
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
